import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Plus, ChevronLeft, ChevronRight, X, Users,
  Check, Trash2, CheckCircle, AlertCircle,
  Calendar, Building2, Search, Edit2, Shield, Repeat, CalendarDays,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const LES_TYPES = [
  "Personal Training",
  "Duo Personal Training",
  "Small Group Training",
  "Groepstraining",
  "Bootcamp",
  "HIIT Training",
  "CrossFit",
  "Krachttraining",
  "Conditietraining",
  "Circuit Training",
  "Functional Fitness",
  "Yoga",
  "Yoga Flow",
  "Pilates",
  "Stretching & Mobility",
  "Spinning / Indoor Cycling",
  "Box & Kickboksen",
  "Kettlebell Training",
  "Online Sessie",
  "Voedingscoaching",
];

const DUUR_OPTIES = [30, 45, 60, 75, 90, 120];

// ── Kalender helpers ──────────────────────────────────────────────────────────
const PX_PER_MIN  = 1.1;
const START_HOUR  = 7;
const END_HOUR    = 22;
const GRID_HEIGHT = (END_HOUR - START_HOUR) * 60 * PX_PER_MIN;
const HOUR_H      = 60 * PX_PER_MIN;

const TRAINER_COLORS = [
  "#3B82F6","#EF4444","#10B981","#8B5CF6",
  "#F59E0B","#EC4899","#14B8A6","#F97316",
  "#6366F1","#06B6D4","#A855F7","#84CC16",
];

function trainerColor(naam) {
  if (!naam?.trim()) return "#C9A84C";
  let h = 0;
  for (let i = 0; i < naam.length; i++) h = naam.charCodeAt(i) + ((h << 5) - h);
  return TRAINER_COLORS[Math.abs(h) % TRAINER_COLORS.length];
}
function isLight(hex) {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return (r*299+g*587+b*114)/1000 > 150;
}
function timeToMin(t="00:00") { const [h,m]=t.split(":").map(Number); return h*60+m; }
function getMaandag(d=new Date()) { const r=new Date(d); r.setHours(0,0,0,0); const day=r.getDay(); r.setDate(r.getDate()-(day===0?6:day-1)); return r; }
function addDays(d,n) { const r=new Date(d); r.setDate(r.getDate()+n); return r; }
function toStr(d) { return d.toISOString().split("T")[0]; }
function fmtKop(d) { return d.toLocaleDateString("nl-NL",{weekday:"short",day:"numeric",month:"short"}); }

const WEEKDAGEN = ["ma","di","wo","do","vr","za","zo"];

// ── Les-blok ──────────────────────────────────────────────────────────────────
function LesBlok({ les, onClick }) {
  const bg  = les.status === "geannuleerd" ? "#555" : trainerColor(les.instructeur_naam);
  const txt = isLight(bg) ? "#000" : "#fff";
  const top = (timeToMin(les.start_tijd) - START_HOUR*60)*PX_PER_MIN;
  const h   = Math.max(les.duur_minuten*PX_PER_MIN, 36);
  const isVol = les.boekingen_count >= les.max_deelnemers;
  return (
    <button onClick={()=>onClick(les)} data-testid={`admin-les-${les.id}`}
      style={{top,height:h,background:bg,color:txt,position:"absolute",left:1,right:1,zIndex:10}}
      className="rounded-lg px-2 py-1 text-left overflow-hidden hover:brightness-110 active:scale-[0.98] transition-all shadow-md cursor-pointer">
      <p className="font-black truncate leading-none" style={{fontSize:h>48?"11px":"9px"}}>{les.naam}</p>
      {h>38&&<p className="opacity-85 truncate leading-tight mt-0.5" style={{fontSize:"9px"}}>{les.start_tijd}·{les.duur_minuten}m</p>}
      {h>54&&les.instructeur_naam&&<p className="opacity-75 truncate leading-tight" style={{fontSize:"9px"}}>{les.instructeur_naam}</p>}
      {h>68&&<p className="opacity-70" style={{fontSize:"9px"}}>{les.boekingen_count}/{les.max_deelnemers}{isVol?" VOL":""}</p>}
    </button>
  );
}

function TijdKolom() {
  return (
    <div className="shrink-0 w-12 relative select-none" style={{height:GRID_HEIGHT}}>
      {Array.from({length:END_HOUR-START_HOUR+1},(_,i)=>{
        const uur=START_HOUR+i;
        return <div key={uur} className="absolute right-2 flex items-start" style={{top:i*HOUR_H-8}}>
          <span className="text-zinc-600 text-[10px] font-semibold tabular-nums">{String(uur).padStart(2,"0")}:00</span>
        </div>;
      })}
    </div>
  );
}

function GridLines() {
  return <div className="absolute inset-0 pointer-events-none">
    {Array.from({length:END_HOUR-START_HOUR},(_,i)=>(
      <div key={i} className="absolute w-full border-t border-white/5" style={{top:i*HOUR_H}}/>
    ))}
  </div>;
}

// ── LES FORMULIER MODAL ───────────────────────────────────────────────────────
function LesFormModal({ les, bedrijven, fitsmithId, instructeurs, token, onClose, onSaved }) {
  const isEdit = !!les && !les._defaultCompanyId;
  const defaultCompanyId = les?._defaultCompanyId || les?.company_id || fitsmithId || bedrijven[0]?.id || "";
  const defaultNaam = les?.naam
    ? (LES_TYPES.includes(les.naam) ? les.naam : "Aangepast")
    : LES_TYPES[0];
  const defaultAangepast = les?.naam && !LES_TYPES.includes(les.naam) ? les.naam : "";
  const defaultInstr = les?.instructeur_naam
    ? (instructeurs.includes(les.instructeur_naam) ? les.instructeur_naam : (instructeurs[0] || ""))
    : (instructeurs[0] || "");
  const defaultAangepastInstr = les?.instructeur_naam && !instructeurs.includes(les.instructeur_naam)
    ? les.instructeur_naam : "";

  const [form, setForm] = useState({
    company_id:       defaultCompanyId,
    naam:             defaultNaam,
    aangepastNaam:    defaultAangepast,
    datum:            les?.datum || toStr(new Date()),
    start_tijd:       les?.start_tijd || "09:00",
    duur_minuten:     les?.duur_minuten || 60,
    max_deelnemers:   les?.max_deelnemers || 10,
    credits_kosten:   les?.credits_kosten ?? 0,
    instructeur_naam: defaultInstr,
    aangepastInstr:   defaultAangepastInstr,
    locatie:          les?.locatie || "",
    herhalen_weken:   1, // 1 = geen herhaling, >1 = N weken
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = "w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40";

  const handleSave = async () => {
    const finalNaam   = form.naam === "Aangepast" ? form.aangepastNaam.trim() : form.naam;
    const finalInstr  = form.instructeur_naam === "Aangepast" ? form.aangepastInstr.trim() : form.instructeur_naam;
    if (!finalNaam || !form.datum || !form.start_tijd || !form.company_id) return;
    setSaving(true);
    const body = {
      company_id: form.company_id, naam: finalNaam, datum: form.datum,
      start_tijd: form.start_tijd, duur_minuten: Number(form.duur_minuten),
      max_deelnemers: Number(form.max_deelnemers), credits_kosten: Number(form.credits_kosten),
      instructeur_naam: finalInstr, locatie: form.locatie,
      herhalen_weken: !isEdit ? Number(form.herhalen_weken) : 1,
    };
    const url    = isEdit ? `${API}/api/admin/schedule/lessen/${les.id}` : `${API}/api/admin/schedule/lessen`;
    const method = isEdit ? "PUT" : "POST";
    await fetch(url, { method, headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`}, body:JSON.stringify(body) });
    setSaving(false);
    onSaved(isEdit);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 p-4"
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 pb-3 border-b border-white/8">
          <div>
            <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-widest">{isEdit ? "Bewerken" : "Nieuw"}</p>
            <h3 className="text-white font-bold text-lg">{isEdit ? "Les bewerken" : "Les toevoegen"}</h3>
          </div>
          <button onClick={onClose}><X size={18} className="text-zinc-500"/></button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Bedrijf */}
          <div>
            <label className="text-zinc-400 text-xs font-semibold block mb-1.5">Bedrijf</label>
            <select value={form.company_id} onChange={e=>set("company_id",e.target.value)}
              data-testid="les-bedrijf-select" className={inp + " cursor-pointer"}>
              {bedrijven.map(b=><option key={b.id} value={b.id}>{b.naam}</option>)}
            </select>
          </div>

          {/* Type les */}
          <div>
            <label className="text-zinc-400 text-xs font-semibold block mb-1.5">Type les *</label>
            <select value={form.naam}
              onChange={e=>set("naam",e.target.value)}
              data-testid="les-naam-select" className={inp + " cursor-pointer"}>
              {LES_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
              <option value="Aangepast">Aangepast...</option>
            </select>
            {form.naam === "Aangepast" && (
              <input value={form.aangepastNaam} onChange={e=>set("aangepastNaam",e.target.value)}
                placeholder="Typ les naam..." data-testid="les-naam-custom"
                className={inp + " mt-2"} />
            )}
          </div>

          {/* Datum + Starttijd */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-400 text-xs font-semibold block mb-1.5">Datum *</label>
              <input type="date" value={form.datum} onChange={e=>set("datum",e.target.value)}
                data-testid="les-datum-input" className={inp} />
            </div>
            <div>
              <label className="text-zinc-400 text-xs font-semibold block mb-1.5">Starttijd *</label>
              <input type="time" value={form.start_tijd} onChange={e=>set("start_tijd",e.target.value)}
                data-testid="les-tijd-input" className={inp} />
            </div>
          </div>

          {/* Duur + Max deelnemers */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-400 text-xs font-semibold block mb-1.5">Duur</label>
              <select value={form.duur_minuten} onChange={e=>set("duur_minuten",+e.target.value)}
                data-testid="les-duur-select" className={inp + " cursor-pointer"}>
                {DUUR_OPTIES.map(d=><option key={d} value={d}>{d} min</option>)}
              </select>
            </div>
            <div>
              <label className="text-zinc-400 text-xs font-semibold block mb-1.5">Max. deelnemers</label>
              <input type="number" min={1} max={100} value={form.max_deelnemers}
                onChange={e=>set("max_deelnemers",+e.target.value)}
                data-testid="les-max-input" className={inp} />
            </div>
          </div>

          {/* Instructeur */}
          <div>
            <label className="text-zinc-400 text-xs font-semibold block mb-1.5">Instructeur</label>
            <select value={form.instructeur_naam}
              onChange={e=>set("instructeur_naam",e.target.value)}
              data-testid="les-instructeur-select" className={inp + " cursor-pointer"}>
              {instructeurs.map(n=><option key={n} value={n}>{n}</option>)}
              <option value="Aangepast">Aangepast...</option>
            </select>
            {form.instructeur_naam === "Aangepast" && (
              <input value={form.aangepastInstr} onChange={e=>set("aangepastInstr",e.target.value)}
                placeholder="Naam instructeur..." data-testid="les-instructeur-custom"
                className={inp + " mt-2"} />
            )}
          </div>

          {/* Locatie */}
          <div>
            <label className="text-zinc-400 text-xs font-semibold block mb-1.5">
              Locatie <span className="text-zinc-600">(optioneel)</span>
            </label>
            <input value={form.locatie} onChange={e=>set("locatie",e.target.value)}
              placeholder="Bijv. Studio 1, Online, ..." data-testid="les-locatie-input"
              className={inp} />
          </div>

          {/* Credits */}
          <div>
            <label className="text-zinc-400 text-xs font-semibold block mb-1.5">
              Credits kosten <span className="text-zinc-600">(0 = gratis)</span>
            </label>
            <input type="number" min={0} value={form.credits_kosten}
              onChange={e=>set("credits_kosten",+e.target.value)}
              data-testid="les-credits-input" className={inp} />
          </div>

          {/* Herhaling — alleen bij nieuw aanmaken */}
          {!isEdit && (
            <div className="bg-[#1a1a1a] border border-white/8 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Repeat size={13} className="text-[#C9A84C]"/>
                  <span className="text-white text-sm font-semibold">Wekelijks herhalen</span>
                </div>
                <button onClick={()=>set("herhalen_weken", form.herhalen_weken > 1 ? 1 : 4)}
                  data-testid="herhalen-toggle"
                  className={`relative w-11 h-6 rounded-full transition-all ${form.herhalen_weken > 1 ? "" : "bg-white/10"}`}
                  style={{background: form.herhalen_weken > 1 ? "#C9A84C" : ""}}>
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${form.herhalen_weken > 1 ? "left-5" : "left-0.5"}`}/>
                </button>
              </div>
              {form.herhalen_weken > 1 && (
                <>
                  <div>
                    <label className="text-zinc-500 text-[10px] font-semibold block mb-1.5">Aantal weken</label>
                    <div className="flex gap-2 flex-wrap">
                      {[2, 4, 6, 8, 12, 26, 52].map(w=>(
                        <button key={w} onClick={()=>set("herhalen_weken",w)}
                          data-testid={`herhalen-${w}`}
                          className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            form.herhalen_weken===w?"text-black":"bg-white/5 text-zinc-500 hover:text-white"
                          }`}
                          style={form.herhalen_weken===w?{background:"#C9A84C"}:{}}>
                          {w}w
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Preview datums */}
                  <div className="bg-[#111] rounded-xl p-3">
                    <p className="text-zinc-500 text-[10px] font-semibold mb-1.5">
                      Maakt {form.herhalen_weken} lessen aan:
                    </p>
                    <p className="text-zinc-400 text-[10px] leading-relaxed">
                      {Array.from({length: Math.min(form.herhalen_weken, 5)}, (_, i) => {
                        const d = new Date(form.datum + "T12:00:00");
                        d.setDate(d.getDate() + i * 7);
                        return d.toLocaleDateString("nl-NL", {weekday:"short", day:"numeric", month:"short"});
                      }).join(" · ")}
                      {form.herhalen_weken > 5 && ` · ... +${form.herhalen_weken - 5} meer`}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Serie info bij bewerken */}
          {isEdit && les?.serie_id && (
            <div className="bg-[#C9A84C]/8 border border-[#C9A84C]/20 rounded-xl p-3 flex items-center gap-2">
              <Repeat size={12} className="text-[#C9A84C] shrink-0"/>
              <p className="text-zinc-400 text-xs">
                Dit is een herhalende les. Wijzigingen gelden <strong className="text-white">alleen voor deze les</strong>.
                <button className="text-[#C9A84C] ml-1 font-bold hover:underline" data-testid="bewerk-serie-btn"
                  onClick={()=>onSaved("serie", les.serie_id, { ...form })}>
                  Wijzig alle toekomstige lessen →
                </button>
              </p>
            </div>
          )}
        </div>

        <div className="p-5 pt-3 border-t border-white/8 flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold hover:text-white transition-colors">
            Annuleren
          </button>
          <button onClick={handleSave}
            disabled={saving || (form.naam === "Aangepast" && !form.aangepastNaam.trim())}
            data-testid="les-opslaan-btn"
            className="flex-[2] py-2.5 rounded-xl text-black font-bold text-sm disabled:opacity-40 transition-all"
            style={{background:"#C9A84C"}}>
            {saving ? "Opslaan..." : isEdit ? "Wijzigingen opslaan" : "Les toevoegen"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── TOEGANG MODAL ─────────────────────────────────────────────────────────────
function ToegangModal({ token, onClose }) {
  const [gebruikers, setGebruikers] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [zoek,       setZoek]       = useState("");
  const [saving,     setSaving]     = useState(null);

  useEffect(() => {
    fetch(`${API}/api/admin/users`, { headers:{Authorization:`Bearer ${token}`} })
      .then(r=>r.json())
      .then(d=>{ setGebruikers(Array.isArray(d)?d:[]); setLoading(false); })
      .catch(()=>setLoading(false));
  }, [token]);

  const handleToggle = async (user) => {
    const newVal = !user.persoonlijk_rooster_enabled;
    setSaving(user.id);
    await fetch(`${API}/api/admin/users/${user.id}/rooster-toggle`, {
      method:"PUT",
      headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`},
      body:JSON.stringify({enabled:newVal}),
    }).catch(()=>{});
    setGebruikers(g=>g.map(u=>u.id===user.id?{...u,persoonlijk_rooster_enabled:newVal}:u));
    setSaving(null);
  };

  const gefilterd = gebruikers.filter(u=>
    u.name?.toLowerCase().includes(zoek.toLowerCase()) ||
    u.email?.toLowerCase().includes(zoek.toLowerCase())
  );
  const actief = gebruikers.filter(u=>u.persoonlijk_rooster_enabled).length;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 p-4"
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-lg max-h-[88vh] flex flex-col">
        <div className="h-1.5 rounded-t-2xl" style={{background:"#C9A84C"}}/>
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-widest">FitSmith Rooster</p>
            <h3 className="text-white font-black text-lg leading-tight">Rooster-toegang beheren</h3>
            <p className="text-zinc-500 text-xs mt-0.5">{actief} van {gebruikers.length} cursisten hebben toegang</p>
          </div>
          <button onClick={onClose}><X size={18} className="text-zinc-500"/></button>
        </div>

        <div className="px-5 pb-3">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600"/>
            <input value={zoek} onChange={e=>setZoek(e.target.value)} placeholder="Zoek cursist..."
              data-testid="toegang-zoek"
              className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl pl-9 pr-4 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/40"/>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-5 pb-5">
          {loading ? (
            <div className="h-24 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin"/>
            </div>
          ) : (
            <div className="space-y-1.5">
              {gefilterd.map(u=>{
                const heeft = u.persoonlijk_rooster_enabled;
                const isSaving = saving === u.id;
                return (
                  <div key={u.id} data-testid={`toegang-row-${u.id}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/3 transition-colors">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-black text-black shrink-0"
                      style={{background:heeft?"#C9A84C":"#333"}}>
                      {u.name?.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()||"?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{u.name}</p>
                      <p className="text-zinc-500 text-xs truncate">{u.email}</p>
                    </div>
                    <span className={`text-xs font-bold ${heeft?"text-emerald-400":"text-zinc-600"}`}>
                      {heeft?"Toegang":"Geen toegang"}
                    </span>
                    <button onClick={()=>handleToggle(u)} disabled={isSaving}
                      data-testid={`toggle-toegang-${u.id}`}
                      className={`relative w-11 h-6 rounded-full transition-all disabled:opacity-40 ${heeft?"":"bg-white/10"}`}
                      style={{background:heeft?"#C9A84C":""}}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${heeft?"left-5":"left-0.5"}`}/>
                    </button>
                  </div>
                );
              })}
              {gefilterd.length === 0 && !loading && (
                <p className="text-zinc-500 text-sm text-center py-6">Geen cursisten gevonden</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── DEELNEMERS MODAL ──────────────────────────────────────────────────────────
function DeelnemersModal({ les, fitsmithId, token, onClose, onEdit, onDelete }) {
  const [boekingen,     setBoekingen]     = useState([]);
  const [sporters,      setSporters]      = useState([]);
  const [zoek,          setZoek]          = useState("");
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(null);
  const [toast,         setToast]         = useState(null);
  const [confirmDel,    setConfirmDel]    = useState(false);
  const [doorplanUsers, setDoorplanUsers] = useState({}); // userId -> {loading, done, ingepland}

  const showToast = (msg,t="success")=>{ setToast({msg,t}); setTimeout(()=>setToast(null),3000); };
  const isFitSmith = les.company_id === fitsmithId;
  const heeftSerie = !!les.serie_id;

  const load = useCallback(async () => {
    setLoading(true);
    const boekRes = await fetch(`${API}/api/admin/schedule/lessen/${les.id}/boekingen`,
      { headers:{Authorization:`Bearer ${token}`} });
    const boekD = await boekRes.json();
    setBoekingen(boekD.boekingen || []);

    let lijst = [];
    if (isFitSmith) {
      // FitSmith: B2C cursisten met rooster-toegang
      const r = await fetch(`${API}/api/admin/users`, { headers:{Authorization:`Bearer ${token}`} });
      const data = await r.json();
      lijst = (Array.isArray(data)?data:[])
        .filter(u=>u.persoonlijk_rooster_enabled)
        .map(u=>({id:u.id,name:u.name||"",email:u.email||""}));
    } else if (les.company_id) {
      const r = await fetch(`${API}/api/admin/companies/${les.company_id}/sporters`,
        { headers:{Authorization:`Bearer ${token}`} });
      const data = await r.json();
      lijst = (Array.isArray(data)?data:[]).map(s=>({id:s.id,name:s.naam||s.name||"",email:s.email||""}));
    } else {
      const r = await fetch(`${API}/api/admin/users`, { headers:{Authorization:`Bearer ${token}`} });
      const data = await r.json();
      lijst = (Array.isArray(data)?data:[]).map(u=>({id:u.id,name:u.name||"",email:u.email||""}));
    }
    setSporters(lijst);
    setLoading(false);
  }, [les.id, les.company_id, token, isFitSmith]);

  useEffect(()=>{ load(); },[load]);

  const inIds = new Set(boekingen.filter(b=>b.status==="bevestigd").map(b=>b.sporter_id));
  const wlIds = new Set(boekingen.filter(b=>b.status==="wachtlijst").map(b=>b.sporter_id));

  const handleToggle = async (user) => {
    const isIn = inIds.has(user.id);
    setSaving(user.id);
    try {
      if (isIn) {
        const b = boekingen.find(b=>b.sporter_id===user.id&&b.status==="bevestigd");
        await fetch(`${API}/api/admin/schedule/boekingen/${b.id}`,
          {method:"DELETE",headers:{Authorization:`Bearer ${token}`}});
        showToast(`${user.name} uitgeschreven`);
      } else {
        const r = await fetch(`${API}/api/admin/schedule/lessen/${les.id}/boekingen`, {
          method:"POST",
          headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`},
          body:JSON.stringify({user_id:user.id}),
        });
        if (!r.ok) { const d=await r.json(); throw new Error(d.detail); }
        showToast(`${user.name} ingeschreven`);
      }
      await load();
    } catch(e) { showToast(e.message,"error"); }
    setSaving(null);
  };

  const handleDelete = async (deleteSerie = false) => {
    if (deleteSerie && les.serie_id) {
      await fetch(`${API}/api/admin/schedule/serie/${les.serie_id}`,
        {method:"DELETE",headers:{Authorization:`Bearer ${token}`}});
    } else {
      await fetch(`${API}/api/admin/schedule/lessen/${les.id}`,
        {method:"DELETE",headers:{Authorization:`Bearer ${token}`}});
    }
    onDelete();
  };

  const handleDoorplannen = async (user) => {
    setDoorplanUsers(p=>({...p,[user.id]:{loading:true}}));
    try {
      const r = await fetch(`${API}/api/admin/schedule/lessen/${les.id}/boekingen/doorplannen`, {
        method:"POST",
        headers:{"Content-Type":"application/json",Authorization:`Bearer ${token}`},
        body:JSON.stringify({user_id:user.id}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail||"Fout");
      setDoorplanUsers(p=>({...p,[user.id]:{loading:false,done:true,ingepland:d.ingepland}}));
      showToast(`${user.name} ingepland voor ${d.ingepland} lessen`);
    } catch(e) {
      setDoorplanUsers(p=>({...p,[user.id]:{loading:false,done:false}}));
      showToast(e.message,"error");
    }
  };

  const gefilterd = sporters.filter(u=>
    u.name?.toLowerCase().includes(zoek.toLowerCase()) ||
    u.email?.toLowerCase().includes(zoek.toLowerCase())
  );
  const bg = trainerColor(les.instructeur_naam);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 p-4"
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-lg max-h-[88vh] flex flex-col">
        <div className="h-1.5 rounded-t-2xl" style={{background:bg}}/>

        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <h3 className="text-white font-black text-lg leading-tight">{les.naam}</h3>
              {heeftSerie && (
                <span className="flex items-center gap-1 bg-[#C9A84C]/15 text-[#C9A84C] text-[10px] font-bold px-2 py-0.5 rounded-full">
                  <Repeat size={9}/> Herhalend
                </span>
              )}
            </div>
            <p className="text-zinc-400 text-sm">
              {les.datum} · {les.start_tijd} · {les.duur_minuten}min
              {les.locatie&&` · ${les.locatie}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={()=>onEdit(les)} data-testid="les-bewerken-btn"
              className="w-8 h-8 rounded-xl flex items-center justify-center bg-white/5 border border-white/10 text-zinc-400 hover:text-white transition-colors">
              <Edit2 size={13}/>
            </button>
            <button onClick={()=>setConfirmDel(true)} data-testid="les-verwijderen-btn"
              className="w-8 h-8 rounded-xl flex items-center justify-center bg-white/5 border border-white/10 text-zinc-400 hover:text-red-400 transition-colors">
              <Trash2 size={13}/>
            </button>
            <button onClick={onClose}><X size={18} className="text-zinc-500 ml-1"/></button>
          </div>
        </div>

        {confirmDel && (
          <div className="mx-5 mb-3 p-3 rounded-xl bg-red-950/50 border border-red-500/30 space-y-2">
            <p className="text-red-300 text-sm font-semibold">Wat wil je verwijderen?</p>
            <div className="flex flex-col gap-1.5">
              <button onClick={()=>handleDelete(false)} data-testid="confirm-delete-btn"
                className="w-full px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-500 text-left">
                Alleen deze les
              </button>
              {heeftSerie && (
                <button onClick={()=>handleDelete(true)} data-testid="confirm-delete-serie-btn"
                  className="w-full px-3 py-2 rounded-lg bg-red-800 text-white text-xs font-bold hover:bg-red-700 text-left">
                  Alle toekomstige lessen in de serie
                </button>
              )}
              <button onClick={()=>setConfirmDel(false)}
                className="px-3 py-2 rounded-lg bg-white/5 text-zinc-400 text-xs hover:text-white text-left">
                Annuleer
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="px-5 pb-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 bg-[#C9A84C]/15 text-[#C9A84C] rounded-full px-3 py-1.5 text-xs font-bold">
            <Users size={11}/> {boekingen.filter(b=>b.status==="bevestigd").length}/{les.max_deelnemers} ingeschreven
          </div>
          {boekingen.filter(b=>b.status==="wachtlijst").length>0&&(
            <div className="bg-yellow-500/10 text-yellow-400 rounded-full px-3 py-1.5 text-xs font-bold">
              +{boekingen.filter(b=>b.status==="wachtlijst").length} wachtlijst
            </div>
          )}
        </div>

        {/* Zoek */}
        <div className="px-5 pb-3">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600"/>
            <input value={zoek} onChange={e=>setZoek(e.target.value)}
              placeholder={isFitSmith?"Zoek cursist (met rooster-toegang)...":"Zoek sporter..."}
              data-testid="deelnemers-zoek"
              className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl pl-9 pr-4 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/40"/>
          </div>
        </div>

        {/* Lijst */}
        <div className="overflow-y-auto flex-1 px-5 pb-5">
          {loading ? (
            <div className="h-24 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin"/>
            </div>
          ) : gefilterd.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-zinc-500 text-sm">
                {isFitSmith && sporters.length === 0
                  ? "Geen cursisten hebben rooster-toegang. Gebruik de 'Toegang' knop om cursisten toe te voegen."
                  : "Geen sporters gevonden"}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {gefilterd.map(u=>{
                const isIn = inIds.has(u.id);
                const isWl = wlIds.has(u.id);
                const isSaving = saving === u.id;
                return (
                  <div key={u.id} data-testid={`deelnemer-row-${u.id}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/3 transition-colors">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-black text-black shrink-0"
                      style={{background:isIn?"#C9A84C":"#333"}}>
                      {u.name?.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()||"?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{u.name}</p>
                      <p className="text-zinc-500 text-xs truncate">{u.email}</p>
                    </div>
                    {isWl&&<span className="text-yellow-400 text-xs font-bold">Wachtlijst</span>}
                    {/* Doorplannen knop (alleen voor serie-lessen) */}
                    {heeftSerie && isIn && (
                      <button onClick={()=>!doorplanUsers[u.id]?.done && handleDoorplannen(u)}
                        disabled={doorplanUsers[u.id]?.loading}
                        data-testid={`doorplannen-${u.id}`}
                        title="Doorplannen voor alle komende lessen"
                        className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-all ${
                          doorplanUsers[u.id]?.done
                            ? "bg-emerald-500/20 text-emerald-400"
                            : "bg-white/5 border border-white/10 text-zinc-500 hover:text-[#C9A84C] hover:border-[#C9A84C]/30"
                        } disabled:opacity-40`}>
                        {doorplanUsers[u.id]?.loading
                          ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"/>
                          : doorplanUsers[u.id]?.done
                            ? <CheckCircle size={11}/>
                            : <CalendarDays size={11}/>}
                      </button>
                    )}
                    <button onClick={()=>handleToggle(u)} disabled={isSaving}
                      data-testid={`toggle-deelnemer-${u.id}`}
                      className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-all disabled:opacity-40 ${
                        isIn?"bg-[#C9A84C] text-black":"bg-white/8 border border-white/12 text-zinc-500 hover:border-[#C9A84C]/40 hover:text-[#C9A84C]"
                      }`}>
                      {isSaving
                        ? <div className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin"/>
                        : <Check size={13}/>}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {toast && (
          <div className={`mx-5 mb-4 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold ${
            toast.t==="success"?"bg-[#1a1a1a] border border-[#C9A84C]/30 text-white":"bg-red-950 border border-red-500/30 text-red-300"
          }`}>
            {toast.t==="success"?<CheckCircle size={13} className="text-[#C9A84C]"/>:<AlertCircle size={13} className="text-red-400"/>}
            {toast.msg}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Trainer legenda ───────────────────────────────────────────────────────────
function TrainerLegenda({ trainers }) {
  if (!trainers.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {trainers.map(naam=>{
        const bg=trainerColor(naam);
        return (
          <div key={naam} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{background:bg+"22",border:`1px solid ${bg}55`}}>
            <div className="w-2 h-2 rounded-full" style={{background:bg}}/>
            <span style={{color:bg}}>{naam}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── HOOFD COMPONENT ───────────────────────────────────────────────────────────
export default function AdminRoosterTab({ token }) {
  const [lessen,       setLessen]       = useState([]);
  const [bedrijven,    setBedrijven]    = useState([]);
  const [fitsmithId,   setFitsmithId]   = useState("");
  const [instructeurs, setInstructeurs] = useState(["Samuel van Grondelle"]);
  const [bedrijfId,    setBedrijfId]    = useState("");
  const [loading,      setLoading]      = useState(true);
  const [weekStart,    setWeekStart]    = useState(()=>getMaandag());
  const [activeDag,    setActiveDag]    = useState(()=>new Date());
  const [detailLes,    setDetailLes]    = useState(null);
  const [showLesModal, setShowLesModal] = useState(false);
  const [editLes,      setEditLes]      = useState(null); // null = nieuw, les-obj = bewerken
  const [showToegang,  setShowToegang]  = useState(false);
  const [isMobile,     setIsMobile]     = useState(false);
  const [toast,        setToast]        = useState(null);

  useEffect(()=>{
    const check=()=>setIsMobile(window.innerWidth<768);
    check(); window.addEventListener("resize",check);
    return ()=>window.removeEventListener("resize",check);
  },[]);

  const showToast=(msg,t="success")=>{ setToast({msg,t}); setTimeout(()=>setToast(null),3500); };

  const loadInstructeurs = useCallback(()=>{
    fetch(`${API}/api/admin/fitsmith/instructeurs`,{headers:{Authorization:`Bearer ${token}`}})
      .then(r=>r.json()).then(d=>{ if(d.instructeurs?.length) setInstructeurs(d.instructeurs); })
      .catch(()=>{});
  },[token]);

  // Laad FitSmith company + reguliere bedrijven in één keer
  useEffect(()=>{
    const load = async () => {
      const [fsRes, compRes] = await Promise.all([
        fetch(`${API}/api/admin/fitsmith-company`,{headers:{Authorization:`Bearer ${token}`}}),
        fetch(`${API}/api/companies`,{headers:{Authorization:`Bearer ${token}`}}),
      ]);
      const fsData   = await fsRes.json();
      const compData = await compRes.json();
      const fsId     = fsData.id || "";
      if (fsId) setFitsmithId(fsId);
      const regs = (compData.companies||[]).filter(c=>c.slug!=="fitsmith-systeem");
      setBedrijven(fsId ? [{id:fsId,naam:"FitSmith (eigen)"},...regs] : regs);
    };
    load().catch(()=>{});
    loadInstructeurs();
  },[token, loadInstructeurs]);

  const loadLessen = useCallback(async()=>{
    setLoading(true);
    const url = bedrijfId
      ? `${API}/api/admin/schedule/lessen?company_id=${bedrijfId}`
      : `${API}/api/admin/schedule/lessen`;
    const r = await fetch(url,{headers:{Authorization:`Bearer ${token}`}});
    const d = await r.json();
    setLessen(d.lessen||[]);
    setLoading(false);
  },[token,bedrijfId]);

  useEffect(()=>{ loadLessen(); },[loadLessen]);

  const weekDagen = useMemo(()=>Array.from({length:7},(_,i)=>addDays(weekStart,i)),[weekStart]);

  const lessenByDatum = useMemo(()=>{
    const map={};
    lessen.forEach(l=>{ if(!map[l.datum])map[l.datum]=[]; map[l.datum].push(l); });
    return map;
  },[lessen]);

  const trainers = useMemo(()=>
    [...new Set(lessen.map(l=>l.instructeur_naam).filter(Boolean))].sort(),
  [lessen]);

  const weekRange = `${weekDagen[0].toLocaleDateString("nl-NL",{day:"numeric",month:"short"})} – ${weekDagen[6].toLocaleDateString("nl-NL",{day:"numeric",month:"short",year:"numeric"})}`;

  const handleLesOpgeslagen = async (wasEdit) => {
    // wasEdit kan ook "serie" zijn + serie_id + body voor serie-update
    if (wasEdit === "serie") return; // Serie-update wordt apart afgehandeld (zie LesFormModal)
    setShowLesModal(false);
    setEditLes(null);
    setDetailLes(null);
    await loadLessen();
    loadInstructeurs();
    showToast(wasEdit ? "Les bijgewerkt!" : "Les toegevoegd!");
  };

  const handleSerieUpdate = async (serieId, body) => {
    const update = {
      naam: body.naam === "Aangepast" ? body.aangepastNaam : body.naam,
      start_tijd: body.start_tijd, duur_minuten: Number(body.duur_minuten),
      max_deelnemers: Number(body.max_deelnemers), credits_kosten: Number(body.credits_kosten),
      instructeur_naam: body.instructeur_naam === "Aangepast" ? body.aangepastInstr : body.instructeur_naam,
      locatie: body.locatie,
    };
    await fetch(`${API}/api/admin/schedule/serie/${serieId}`, {
      method: "PUT",
      headers: {"Content-Type":"application/json", Authorization:`Bearer ${token}`},
      body: JSON.stringify(update),
    });
    setShowLesModal(false); setEditLes(null); setDetailLes(null);
    await loadLessen();
    showToast("Alle toekomstige lessen bijgewerkt!");
  };

  const handleLesVerwijderd = async () => {
    setDetailLes(null);
    await loadLessen();
    showToast("Les verwijderd");
  };

  // Nieuw-les modal opent met het huidig gefilterd bedrijf als default (of FitSmith als geen filter)
  const openNieuwe = () => {
    const defaultCompany = bedrijfId || fitsmithId;
    setEditLes({ _defaultCompanyId: defaultCompany });
    setShowLesModal(true);
  };
  const openEdit   = (les) => { setDetailLes(null); setEditLes(les); setShowLesModal(true); };

  const toonToegangKnop = !bedrijfId || bedrijfId === fitsmithId;

  // ── Week grid ───────────────────────────────────────────────────────────────
  const WeekGrid = () => (
    <div className="overflow-x-auto">
      <div className="min-w-[720px]">
        <div className="flex border-b border-white/10">
          <div className="w-12 shrink-0"/>
          {weekDagen.map(dag=>{
            const str=toStr(dag); const vandaag=str===toStr(new Date());
            return <div key={str} className="flex-1 text-center py-2.5">
              <span className={`text-xs font-bold uppercase tracking-wide ${vandaag?"text-[#C9A84C]":"text-zinc-400"}`}>{fmtKop(dag)}</span>
              {vandaag&&<div className="w-1 h-1 rounded-full bg-[#C9A84C] mx-auto mt-1"/>}
            </div>;
          })}
        </div>
        <div className="flex overflow-y-auto max-h-[65vh]">
          <TijdKolom/>
          {weekDagen.map(dag=>{
            const str=toStr(dag); const dagLessen=lessenByDatum[str]||[];
            return <div key={str} className="flex-1 relative border-l border-white/5" style={{height:GRID_HEIGHT}}>
              <GridLines/>
              {dagLessen.map(les=><LesBlok key={les.id} les={les} onClick={setDetailLes}/>)}
            </div>;
          })}
        </div>
      </div>
    </div>
  );

  const scrollRef = useRef(null);
  const DagTabsView = () => {
    const dagLessen = lessenByDatum[toStr(activeDag)]||[];
    return (
      <div>
        <div ref={scrollRef} className="flex gap-2 overflow-x-auto pb-2 mb-4">
          {weekDagen.map(dag=>{
            const str=toStr(dag); const isActive=str===toStr(activeDag); const vandaag=str===toStr(new Date());
            const heeftLessen=(lessenByDatum[str]||[]).length>0;
            return <button key={str} onClick={()=>setActiveDag(dag)} data-testid={`dag-tab-${str}`}
              className={`shrink-0 flex flex-col items-center px-3.5 py-2 rounded-xl transition-all ${isActive?"bg-[#C9A84C] text-black":"bg-[#111] border border-white/8 text-zinc-400 hover:border-white/20"}`}>
              <span className="text-[10px] font-bold uppercase">{WEEKDAGEN[dag.getDay()===0?6:dag.getDay()-1]}</span>
              <span className={`text-lg font-black leading-none ${isActive?"text-black":vandaag?"text-[#C9A84C]":"text-white"}`}>{dag.getDate()}</span>
              {heeftLessen&&!isActive&&<div className="w-1.5 h-1.5 rounded-full mt-0.5 bg-[#C9A84C]"/>}
            </button>;
          })}
        </div>
        <div className="overflow-y-auto max-h-[65vh]">
          {dagLessen.length===0 ? (
            <div className="bg-[#111] border border-white/6 rounded-2xl p-10 text-center">
              <Calendar size={24} className="text-zinc-700 mx-auto mb-2"/>
              <p className="text-zinc-500 text-sm font-semibold">Geen lessen op deze dag</p>
            </div>
          ) : (
            <div className="flex">
              <TijdKolom/>
              <div className="flex-1 relative border-l border-white/5" style={{height:GRID_HEIGHT}}>
                <GridLines/>
                {dagLessen.map(les=><LesBlok key={les.id} les={les} onClick={setDetailLes}/>)}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-0.5">Beheer</p>
          <h2 className="text-xl font-bold text-white">Rooster</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {toonToegangKnop && fitsmithId && (
            <button onClick={()=>setShowToegang(true)} data-testid="toegang-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 text-sm font-semibold transition-all">
              <Shield size={13}/> Toegang
            </button>
          )}
          {bedrijven.length > 0 && (
            <div className="flex items-center gap-2">
              <Building2 size={14} className="text-zinc-500"/>
              <select value={bedrijfId} onChange={e=>setBedrijfId(e.target.value)}
                data-testid="bedrijf-filter"
                className="bg-[#111] border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C9A84C]/40 cursor-pointer">
                <option value="">Alle bedrijven</option>
                {bedrijven.map(b=><option key={b.id} value={b.id}>{b.naam}</option>)}
              </select>
            </div>
          )}
          <button onClick={openNieuwe} data-testid="les-toevoegen-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-black text-sm font-bold transition-all hover:brightness-110"
            style={{background:"#C9A84C"}}>
            <Plus size={14}/> Les toevoegen
          </button>
        </div>
      </div>

      {/* Week navigatie */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={()=>setWeekStart(w=>addDays(w,-7))} data-testid="prev-week-btn"
          className="flex items-center gap-1 text-sm font-semibold text-zinc-400 hover:text-white transition-colors px-3 py-2 rounded-xl hover:bg-white/5">
          <ChevronLeft size={16}/> Vorige
        </button>
        <span className="text-white text-sm font-bold">{weekRange}</span>
        <button onClick={()=>setWeekStart(w=>addDays(w,7))} data-testid="next-week-btn"
          className="flex items-center gap-1 text-sm font-semibold text-zinc-400 hover:text-white transition-colors px-3 py-2 rounded-xl hover:bg-white/5">
          Volgende <ChevronRight size={16}/>
        </button>
      </div>

      <TrainerLegenda trainers={trainers}/>

      {/* Kalender */}
      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#C9A84C]/40 border-t-[#C9A84C] rounded-full animate-spin"/>
        </div>
      ) : (
        <div className="bg-[#0f0f0f] border border-white/6 rounded-2xl overflow-hidden">
          <div className="p-4">
            {isMobile ? <DagTabsView/> : <WeekGrid/>}
          </div>
          <div className="flex justify-center pb-3">
            <button onClick={()=>setIsMobile(m=>!m)} data-testid="toggle-view-btn"
              className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors px-4 py-1.5">
              {isMobile?"Weekoverzicht":"Dagweergave"}
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold shadow-2xl ${
          toast.t==="success"?"bg-[#111] border border-[#C9A84C]/40 text-white":"bg-red-950 border border-red-500/40 text-red-300"
        }`}>
          {toast.t==="success"?<CheckCircle size={16} className="text-[#C9A84C]"/>:<AlertCircle size={16} className="text-red-400"/>}
          {toast.msg}
        </div>
      )}

      {detailLes && (
        <DeelnemersModal les={detailLes} fitsmithId={fitsmithId} token={token}
          onClose={()=>setDetailLes(null)} onEdit={openEdit} onDelete={handleLesVerwijderd}/>
      )}

      {showLesModal && (
        <LesFormModal
          les={editLes}
          bedrijven={bedrijven}
          fitsmithId={fitsmithId}
          instructeurs={instructeurs}
          token={token}
          onClose={()=>{ setShowLesModal(false); setEditLes(null); }}
          onSaved={handleLesOpgeslagen}/>
      )}

      {showToegang && (
        <ToegangModal token={token} onClose={()=>setShowToegang(false)}/>
      )}
    </div>
  );
}
